import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

type IssueType =
  | 'price_incorrect'
  | 'calculation_wrong'
  | 'missing_feature'
  | 'other';

const ISSUE_LABELS: Record<IssueType, string> = {
  price_incorrect: 'Price looks incorrect',
  calculation_wrong: 'Calculation seems wrong',
  missing_feature: 'Missing feature / service',
  other: 'Other',
};

type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

interface FeedbackModalProps {
  service: string;
  estimateValue: string;
  onClose: () => void;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL;
if (!API_BASE) throw new Error('VITE_API_BASE_URL is not configured');

export function FeedbackModal({ service, estimateValue, onClose }: FeedbackModalProps) {
  const [issueType, setIssueType] = useState<IssueType>('price_incorrect');
  const [description, setDescription] = useState('');
  const [email, setEmail] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const overlayRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLSelectElement>(null);

  // Trap focus and close on Escape
  useEffect(() => {
    firstFocusRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!description.trim()) return;

    setSubmitState('submitting');
    try {
      const res = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          issueType,
          description: description.trim(),
          email: email.trim() || null,
          url: window.location.href,
          estimateValue,
          service,
          timestamp: new Date().toISOString(),
        }),
      });
      if (!res.ok) throw new Error('Request failed');
      setSubmitState('success');
    } catch {
      setSubmitState('error');
    }
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="feedback-modal-title"
    >
      <div className="w-full max-w-md border rounded shadow-2xl flex flex-col" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: '1px solid var(--outline-variant)' }}>
          <h2 id="feedback-modal-title" className="text-[15px] font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="material-symbols-outlined text-[18px]" style={{ color: 'var(--text-tertiary)' }}>flag</span>
            Report an issue with this estimate
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            aria-label="Close"
          >
            <span className="material-symbols-outlined text-[20px]">close</span>
          </button>
        </div>

        {submitState === 'success' ? (
          <div className="px-6 py-10 flex flex-col items-center gap-4 text-center">
            <span className="material-symbols-outlined text-[#107c10] text-[40px]">check_circle</span>
            <p className="text-[15px] font-semibold" style={{ color: 'var(--text-primary)' }}>Thanks — we'll review this within 48 hours</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Your feedback helps keep estimates accurate.</p>
            <button
              type="button"
              onClick={onClose}
              className="mt-2 px-5 py-2 border text-[13px] rounded transition-colors"
              style={{ backgroundColor: 'var(--surface-container-high)', borderColor: 'var(--outline-variant)', color: 'var(--text-secondary)' }}
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4 px-6 py-5">
            {/* Issue type */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Issue type
              </label>
              <select
                ref={firstFocusRef}
                value={issueType}
                onChange={(e) => setIssueType(e.target.value as IssueType)}
                className="text-[13px] px-3 py-2 rounded focus:outline-none focus:border-[#0078d4] appearance-none cursor-pointer"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              >
                {(Object.entries(ISSUE_LABELS) as [IssueType, string][]).map(([val, label]) => (
                  <option key={val} value={val}>{label}</option>
                ))}
              </select>
            </div>

            {/* Description */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Description <span style={{ color: '#a4262c' }}>*</span>
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value.slice(0, 500))}
                rows={4}
                required
                placeholder="Describe what looks wrong and what you expected…"
                className="text-[13px] px-3 py-2 rounded focus:outline-none focus:border-[#0078d4] resize-none"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              />
              <p className="text-[10px] text-right" style={{ color: 'var(--text-tertiary)' }}>{description.length}/500</p>
            </div>

            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Email <span className="normal-case font-normal" style={{ color: 'var(--text-tertiary)' }}>(optional — for follow-up)</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="text-[13px] px-3 py-2 rounded focus:outline-none focus:border-[#0078d4]"
                style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-primary)' }}
              />
              <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                Your email is optional and only used to follow up on your report. It is deleted after 90 days.{' '}
                <Link to="/privacy" className="text-[#0078d4] hover:underline" onClick={onClose}>Privacy Policy</Link>.
              </p>
            </div>

            {/* Auto-populated fields (read-only) */}
            <div className="flex gap-3">
              <div className="flex-1 flex flex-col gap-1.5">
                <label className="text-[11px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Service</label>
                <div className="text-[12px] px-3 py-2 rounded font-mono" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
                  {service}
                </div>
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <label className="text-[11px] font-mono uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>Estimate</label>
                <div className="text-[12px] px-3 py-2 rounded font-mono" style={{ backgroundColor: 'var(--surface-container-low)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
                  {estimateValue}
                </div>
              </div>
            </div>

            {submitState === 'error' && (
              <p className="text-[12px] text-[#a4262c] flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[15px]">error</span>
                Failed to submit — please try again.
              </p>
            )}

            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-[13px] transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitState === 'submitting' || !description.trim()}
                className="flex items-center gap-2 px-5 py-2 bg-[#0078d4] hover:bg-[#106ebe] disabled:opacity-50 disabled:cursor-not-allowed text-white text-[13px] font-semibold rounded transition-colors"
              >
                {submitState === 'submitting' && (
                  <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                )}
                Submit
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
